// Communications — My Desk panel half of N-094 ("forward and file"). Every
// mapped role sees this (ALL_ROLES) — the server scopes /api/v1/communications
// by role automatically, so there is no client-side role gate here, unlike
// RaiseAlerts/NeedsHireDetailsAlert.
//
// Same loader shape as ComplianceAlerts.tsx: `useResource`, not
// `peekResource`, so this panel fetches (or revalidates a stale mirrored
// copy) on its OWN mount. That is paired with `communicationsHasContent` in
// panelCatalog.tsx, which decides whether DeskGrid mounts this component at
// all — see that file's own comment block on why a `useResource`-backed
// panel must never go on the canvas without a matching `hasContent` (a bare
// fetch-on-mount panel would fire from every test that puts this type on a
// layout without also stubbing the endpoint, same trap BirthdayAlerts.tsx
// documents avoiding).
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { useResource } from '../lib/store';
import { session } from '../lib/session';
import { clinicToday } from '../lib/clinicDay';
import { daysBetweenIso } from '../lib/civilDate';
import {
  COMMUNICATIONS_KEY, fetchCommunications, setCommunicationDeskRetired, type CommunicationsData,
} from '../lib/communications';
import { DeskPanel } from './DeskPanel';

/** Whole clinic days since the email landed (owner, 2026-10-12: a small
 * "Days old" column with just the number, e.g. 14). Null when the date is
 * missing or unreadable — shown as a dash, never a guess. */
export function daysOld(receivedAt: string | null, now: Date = new Date()): number | null {
  if (!receivedAt) return null;
  const then = new Date(receivedAt);
  if (Number.isNaN(then.getTime())) return null;
  return Math.max(0, daysBetweenIso(clinicToday(then), clinicToday(now)));
}

export function CommunicationsPanel(
  { chromeless = false, now = new Date() }: { chromeless?: boolean; now?: Date } = {},
): JSX.Element | null {
  const load = useResource<CommunicationsData>(COMMUNICATIONS_KEY, fetchCommunications);
  const data = load.status === 'ready' ? load.data : null;
  const me = session.value.me;
  const isOwner = Boolean(me && !me.viewingAs && me.role === 'OWNER');
  const [busyId, setBusyId] = useState<number | null>(null);
  async function retire(id: number): Promise<void> {
    if (busyId !== null) return;
    setBusyId(id);
    try {
      await setCommunicationDeskRetired(id, true);
    } finally {
      setBusyId(null);
    }
  }
  // Owner's Retire: off everyone's Desk, still on the Communications page.
  const items = (data?.communications ?? [])
    .filter((c) => !c.deskRetired)
    .sort((a, b) => {
      const ta = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
      const tb = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
      return tb - ta;
    });
  const unreadCount = data?.unreadCount ?? 0;

  if (!items.length) {
    // Same N-090c reasoning as ComplianceAlerts.tsx: only name the empty
    // state on the canvas (chromeless) — outside it, nothing to render yet.
    if (!chromeless) return null;
    return (
      <DeskPanel title="Communications" testId="communications-panel" chromeless>
        <p class="empty">No communications yet.</p>
      </DeskPanel>
    );
  }

  return (
    <DeskPanel
      title="Communications"
      subtitle={unreadCount ? `${unreadCount} unread.` : 'All caught up.'}
      testId="communications-panel"
      chromeless={chromeless}
    >
      {/* Owner, 2026-10-12: rows read like My Mail — subject bold, sender
         muted underneath, and a small "Days old" number on the right
         instead of a wide "14 days ago" label column. */}
      <div class="desk-panel-scroll" data-testid="communications-queue">
        <div class="desk-list-head" data-testid="communications-days-old-head">Days old</div>
        <ul class="desk-list">
          {items.map((c) => {
            const age = daysOld(c.receivedAt, now);
            return (
              <li key={c.id}>
                <a class="desk-row" href={`/company#comm-${c.id}`} data-testid={`communication-row-${c.id}`}>
                  <span class="desk-row-line">
                    <span class="desk-row-main">{c.subject || '(no subject)'}</span>
                    {c.mustRead && c.unread ? <span class="badge warn">MUST READ</span> : null}
                    {c.draft ? <span class="badge">DRAFT</span> : c.whatsNew ? <span class="badge">WHAT'S NEW</span> : null}
                    {c.unread ? <span class="badge">Unread</span> : null}
                    <span class="desk-row-value" data-testid={`communication-days-old-${c.id}`}>
                      {age === null ? '—' : age}
                    </span>
                  </span>
                  <span class="desk-row-sub">
                    {c.fromName || c.fromAddress || 'Unknown sender'}
                    {c.attachmentCount
                      ? ` · ${c.attachmentCount} attachment${c.attachmentCount === 1 ? '' : 's'}`
                      : ''}
                  </span>
                </a>
                {isOwner ? (
                  <button
                    type="button"
                    class="btn"
                    data-testid={`communication-retire-${c.id}`}
                    disabled={busyId !== null}
                    onClick={() => void retire(c.id)}
                  >
                    Retire
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </DeskPanel>
  );
}
